/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * THE DOCUMENT TRANSLATOR RATE, READ FROM THE SHOP'S BACK END.
 *
 *   GET /api/v1/prices/document-translator-rate/
 *   { currency: "usd", base_price_cents, price_per_word_cents, base_word_threshold }
 *
 * The back end charges Document Translator from these three numbers
 * (backend/apps/checkout/document_translator_pricing.py) and publishes them
 * here. The browser keeps no copy of its own: every price it shows for a
 * Document Translator order is computed from what this endpoint answered, so
 * the price quoted and the price charged cannot part.
 *
 * Like the word-credit balance (dtCreditsApi.ts), a screen is told a STATE,
 * not a number:
 *
 *   idle     Nothing asked for it (no Document Translator line in the cart).
 *   loading  The first read has not answered.
 *   known    The rate arrived and is well formed.
 *   unknown  The read failed, or answered something that is not a rate.
 *
 * Only "known" carries a rate, so no screen can show a price it does not have.
 * What each screen does with the other states is written where it reads them.
 */

import { createApi } from "@reduxjs/toolkit/query/react";
import { useCallback, useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import type { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import { baseQueryWithReauth, CACHE_TIMES } from "@/redux/api-config";
import type { DocumentTranslatorRate } from "@/lib/document-translator-pricing";

/** The endpoint's answer, as the back end spells it. */
interface DocumentTranslatorRateResponse {
  currency?: unknown;
  base_price_cents?: unknown;
  price_per_word_cents?: unknown;
  base_word_threshold?: unknown;
  free_words?: { eligible?: unknown; words?: unknown; waive_below_cents?: unknown };
}

/** Session 888 review, FW-1: what the feed says for one computer. */
export interface FreeWordsOffer {
  eligible: boolean;
  words: number;
  waiveBelowCents: number;
}

const isWholeAmount = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0;

/**
 * The rate, or null when the answer is not one. A partial or malformed answer
 * is "unknown", never a price computed from whatever part of it arrived.
 */
export function toDocumentTranslatorRate(
  body: DocumentTranslatorRateResponse | null | undefined,
): DocumentTranslatorRate | null {
  if (!body) return null;
  const { base_price_cents, price_per_word_cents, base_word_threshold } = body;
  if (
    !isWholeAmount(base_price_cents) ||
    !isWholeAmount(price_per_word_cents) ||
    !isWholeAmount(base_word_threshold)
  ) {
    return null;
  }
  return {
    basePriceCents: base_price_cents,
    pricePerWordCents: price_per_word_cents,
    baseWordThreshold: base_word_threshold,
  };
}

export function toFreeWordsOffer(
  body: DocumentTranslatorRateResponse | null | undefined,
): FreeWordsOffer | null {
  const offer = body?.free_words;
  if (!offer || !isWholeAmount(offer.words) || !isWholeAmount(offer.waive_below_cents)) {
    return null;
  }
  return {
    eligible: offer.eligible === true,
    words: offer.words,
    waiveBelowCents: offer.waive_below_cents,
  };
}

export const dtRateApi = createApi({
  reducerPath: "dtRateApi",
  baseQuery: baseQueryWithReauth,
  // The rate changes only with a release of the shop; one read per hour of
  // browsing is plenty, and the server's Cache-Control lets the browser reuse it.
  keepUnusedDataFor: CACHE_TIMES.STABLE,
  endpoints: (builder) => ({
    getDocumentTranslatorRate: builder.query<DocumentTranslatorRate | null, void>({
      query: () => "prices/document-translator-rate/",
      transformResponse: (body: DocumentTranslatorRateResponse) =>
        toDocumentTranslatorRate(body),
    }),
    // The same route with ?machine_id=: read per computer, and never kept.
    getFreeWordsOffer: builder.query<FreeWordsOffer | null, string>({
      query: (machineId) =>
        `prices/document-translator-rate/?machine_id=${encodeURIComponent(machineId)}`,
      keepUnusedDataFor: 0,
      transformResponse: (body: DocumentTranslatorRateResponse) => toFreeWordsOffer(body),
    }),
  }),
});

export type DtRateState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "known"; rate: DocumentTranslatorRate }
  | { status: "unknown"; retrying: boolean };

/** The parts of the cached query the reader needs. */
interface RateQueryResult {
  data?: DocumentTranslatorRate | null;
  isError: boolean;
  isFetching: boolean;
}

/**
 * Turn the cached query into a state. The ORDER is the rule:
 *   1. Not needed          -> idle.
 *   2. Last read failed    -> unknown, even when an earlier read left data
 *      behind: a screen must not keep quoting from an answer the shop can no
 *      longer give (the same rule as the word balance, V-3).
 *   3. A rate arrived      -> known.
 *   4. Something arrived that is not a rate -> unknown.
 *   5. Nothing yet         -> loading (including the render before the read
 *      has been started).
 */
export function readDtRate(query: RateQueryResult, skip: boolean): DtRateState {
  if (skip) return { status: "idle" };
  if (query.isError) return { status: "unknown", retrying: query.isFetching };
  if (query.data) return { status: "known", rate: query.data };
  if (query.data === null) return { status: "unknown", retrying: query.isFetching };
  return { status: "loading" };
}

const getRate = dtRateApi.endpoints.getDocumentTranslatorRate;
const selectRateResult = getRate.select();

/**
 * The cached query, or an empty one when the store has no rate API at all
 * (a narrow test store for a screen that never prices by the word).
 */
function selectRateQuery(
  state: Partial<Record<typeof dtRateApi.reducerPath, unknown>>,
): RateQueryResult {
  if (!state[dtRateApi.reducerPath]) return { isError: false, isFetching: false };
  const result = selectRateResult(state as Parameters<typeof selectRateResult>[0]);
  return {
    data: result.data,
    isError: result.isError,
    isFetching: result.status === "pending",
  };
}

/**
 * The rate for a screen that shows a Document Translator price.
 *
 * `needed` is false when nothing on screen is priced by the word. Then nothing
 * is requested and nothing is subscribed: a cart without Document Translator
 * never asks, and a screen that never prices by the word needs no rate API in
 * its store. (This is RTK Query's own subscribe-and-select pattern, written
 * out because its generated hook requires the API's middleware even when
 * skipped.)
 */
export function useDocumentTranslatorRate(needed: boolean): {
  rate: DtRateState;
  retry: () => void;
} {
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>();
  useEffect(() => {
    if (!needed) return;
    const subscription = dispatch(getRate.initiate());
    return () => subscription.unsubscribe();
  }, [needed, dispatch]);

  const query = useSelector(selectRateQuery, shallowEqual);

  const retry = useCallback(() => {
    if (!needed) return;
    void dispatch(getRate.initiate(undefined, { subscribe: false, forceRefetch: true }));
  }, [needed, dispatch]);

  return { rate: readDtRate(query, !needed), retry };
}

/**
 * The known rate from the store, or null. For the cart's selectors, which
 * price the lines outside any component.
 */
export function selectDocumentTranslatorRate(
  state: Partial<Record<typeof dtRateApi.reducerPath, unknown>>,
): DocumentTranslatorRate | null {
  const query = selectRateQuery(state);
  return query.isError ? null : (query.data ?? null);
}

export type FreeWordsOfferState =
  | { status: "idle" }
  | { status: "loading" }
  | ({ status: "known" } & FreeWordsOffer)
  | { status: "unknown" };

/** The offer for this computer. "" asks for nothing: no job, or a browser identifier. */
export function useFreeWordsOffer(machineId: string): {
  offer: FreeWordsOfferState;
  retry: () => void;
} {
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>();
  const endpoint = dtRateApi.endpoints.getFreeWordsOffer;
  useEffect(() => {
    if (!machineId) return;
    const subscription = dispatch(endpoint.initiate(machineId));
    return () => subscription.unsubscribe();
  }, [dispatch, endpoint, machineId]);
  const query = useSelector(
    (state: Partial<Record<typeof dtRateApi.reducerPath, unknown>>) => {
      if (!machineId || !state[dtRateApi.reducerPath]) return null;
      const result = endpoint.select(machineId)(state as Parameters<ReturnType<typeof endpoint.select>>[0]);
      return { data: result.data, isError: result.isError };
    },
    shallowEqual,
  );
  const retry = useCallback(() => {
    if (machineId) void dispatch(endpoint.initiate(machineId, { forceRefetch: true }));
  }, [dispatch, endpoint, machineId]);

  let offer: FreeWordsOfferState = { status: "idle" };
  if (machineId) {
    // With no rate API in the store there is nothing to wait for.
    if (!query || query.isError || query.data === null) offer = { status: "unknown" };
    else if (query.data) offer = { status: "known", ...query.data };
    else offer = { status: "loading" };
  }
  return { offer, retry };
}
